import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { X } from "lucide-react";
import ispaiLogo from "@/assets/ispai-logo-exec.png";

interface ExecutiveSummaryPopupProps {
  triggerElement: React.ReactNode;
}

export const ExecutiveSummaryPopup: React.FC<ExecutiveSummaryPopupProps> = ({
  triggerElement,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div onClick={() => setIsOpen(true)}>{triggerElement}</div>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 bg-white">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute right-4 top-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        <div className="px-8 py-10 md:px-12">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src={ispaiLogo} alt="iSPAI Logo" className="h-20 w-auto" />
          </div>

          {/* Main Title */}
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-10">
            Investor Overview
          </h1>

          {/* Company Overview */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Company Overview
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                iSPAI ("I Spy") will be the first decentralized knowledge network and marketplace that enables humans to own, attribute, and monetize the knowledge that today is locked inside enterprises and AI vendors. In an AI-driven future, iSPAI positions humans as being "over-the-loop", letting AI represent them rather than simply being "in-the-loop" to train AI to replace them.
              </p>
              <p>
                iSPAI serves enterprises and industry organizations, government agencies, and global workforces by transforming human expertise into structured Knowledge Bases (KBs), which power enterprise AI systems and a decentralized knowledge economy. We see the real value being in the special purpose knowledge base behind the AI, moreso than just the AI Agent itself. And we are building an entirely new economic model with this in mind.
              </p>
              <p>
                iSPAI is building a platform to create, consume, and monetize knowledge for AI. Our mission is to define a new role for humans in an AI-driven future, and we're going to do so by focusing on solving the problem of knowledge entropy.
              </p>
            </div>
          </section>

          {/* Investment Highlights */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Investment Highlights
            </h2>
            <div className="space-y-5 text-gray-700">
              {/* Highlight 1 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1. Strong Market Validation</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>CURT (Construction Users Roundtable) selected iSPAI for a sector-wide POC and pilots.</li>
                  <li>Multiple Fortune-scale industries (Construction, Oil & Gas, Government) have signaled strong interest.</li>
                  <li>Validated enterprise need to combat knowledge entropy - billions lost annually due to turnover, fragmentation, and inability to capture expertise.</li>
                </ul>
              </div>

              {/* Highlight 2 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2. Government Traction</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>The U.S. GSA rated iSPAI a "green AI partner".</li>
                  <li>GSA requested preparation for a 2026 federal collaboration to support a National Knowledge Network initiative.</li>
                </ul>
              </div>

              {/* Highlight 3 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3. Completed MVP & Ready for Early Adopter Deployment</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Full product workflows implemented (for a February 2026 launch):
                    <ul className="list-none ml-6 mt-1 space-y-1">
                      <li>○ Creation: Create → Contribute → Confirm</li>
                      <li>○ Consumption: Compose → Consume → Compensate</li>
                      <li>○ Attribution through smart-contract-ready architecture</li>
                    </ul>
                  </li>
                  <li>First revenue expected early 2026 through enterprise and government pilots.</li>
                </ul>
              </div>

              {/* Highlight 4 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4. High-Caliber Leadership & Advisors</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Founding team includes experts across AI, Web3, enterprise systems, and knowledge engineering.</li>
                  <li>Advisory board includes two retired Fortune 100 O&G CIOs and CURT leadership.</li>
                  <li>iSPAI's Chief Technical Advisor literally wrote the book on Blockchain for Business.</li>
                </ul>
              </div>

              {/* Highlight 5 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">5. Massive Market Opportunity</h3>
                <p className="ml-4 mb-2">iSPAI sits at the intersection of four rapidly scaling sectors:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Enterprise AI</li>
                  <li>Decentralized knowledge ownership</li>
                  <li>Workforce transformation</li>
                  <li>Web3 attribution and tokenized IP</li>
                </ul>
                <p className="ml-4 mt-2">This creates a long-term SaaS, transaction-fee, and marketplace revenue model with significant defensibility.</p>
              </div>

              {/* Highlight 6 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6. Exceptional Capital Efficiency</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Only $175k raised prior to due diligence.</li>
                  <li>Delivered MVP, enterprise commitments, and government adoption signals on minimal burn.</li>
                  <li>Highly capital-efficient technical and go-to-market execution.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Why iSPAI Now */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Why iSPAI Now
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Traditional enterprises are losing billions as knowledge workers retire or churn. AI tools require structured, high-quality domain knowledge - but organizations cannot capture it fast enough.
              </p>
              <p>
                iSPAI solves this by enabling workers to codify, attribute, and own their expertise - turning the workforce from training data for someone else's AI into active participants in the AI economy.
              </p>
              <p>This aligns with macro shifts in:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>AI adoption</li>
                <li>Workforce decentralization</li>
                <li>Web3-enabled digital ownership</li>
                <li>Enterprise demand for knowledge grounding</li>
              </ul>
              <p className="font-medium">iSPAI is uniquely positioned to lead this transformation.</p>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Conclusion
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Recently completed due diligence confirms iSPAI has:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Real traction,</li>
                <li>A working product,</li>
                <li>Enterprise and federal demand, and</li>
                <li>A differentiated model in one of the fastest-growing sectors of the global economy.</li>
              </ul>
              <p className="font-medium">
                iSPAI is an early-stage, high-leverage opportunity with validated need and strong founder-market fit.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="text-center border-t border-gray-200 pt-8">
            <p className="text-gray-700 mb-6">
              For more information or to set up a discussion with iSPAI, please contact Trey Fleming at{" "}
              <a href="mailto:trey.fleming@ispai.io" className="text-blue-600 hover:underline font-medium">
                trey.fleming@ispai.io
              </a>
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
